"use client";

import { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import { Gavel, ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react/dist/ssr';
import { getOrCreateDeviceId } from '@/lib/device-id';
import { getDeviceFingerprint } from '@/lib/device-fingerprint';
import { getAttributionForSignup } from '@/lib/attribution';
import TurnstileWidget from '@/components/TurnstileWidget';
import { trackFbq } from '@/components/analytics/MetaPixel';
import styles from './login.module.css';


// BUG FIX (2026-05): el login NO honraba el `?next=` que el dashboard
// pone cuando una sesión expira (DashboardLayoutClient:115). Click email link
// → /agenda → 401 → /login?next=/agenda → tras login lo manda a /home.
// Deep link perdido. Ahora resolvemos `next` con whitelist de paths internos
// (rechaza protocol-relative o externos para evitar open redirect).
function getSafeRedirectPath(searchParams) {
  const candidate = searchParams.get('next') || searchParams.get('callbackUrl');
  if (!candidate) return '/home';
  // Solo permitimos paths que empiecen con `/` y no `//` (protocol-relative).
  // Esto bloquea `next=https://evil.com/phish` como open redirect.
  if (typeof candidate === 'string' && candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }
  return '/home';
}

function LoginContent() {
  const searchParams = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgSize, setOrgSize] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [country, setCountry] = useState('República Dominicana');
  const [phone, setPhone] = useState('');
  // PR demographics-redesign (2026-05-22): captura demográfica opcional
  // al signup. birth_date deriva age_range automáticamente vía trigger
  // SQL (mig 180). Si user salta, el row se crea sin demografía y puede
  // llenarse después en /configuracion/perfil.
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  // P1 wave 2 (2026-05-24): onboarding personalization fields.
  // Captura especialidad legal + intent primario para personalizar
  // recovery emails y dashboard. Opcionales — si el user salta queda null.
  const [legalSpecialty, setLegalSpecialty] = useState('');
  const [primaryIntent, setPrimaryIntent] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Modal "próximamente" para providers todavía no configurados (Facebook/Apple/etc).
  // Facebook quedó pausado porque la config en Meta for Developers no está
  // completa. Cuando esté lista, basta con remover 'facebook' del set.
  const [comingSoonProvider, setComingSoonProvider] = useState(null);
  // ── Anti-bot in-house (sin servicios externos) ──
  // 1. Honeypot: campo invisible que humanos no tocan; bots autofill todo.
  // 2. Time-to-fill: timestamp del momento en que se monta el form de
  //    registro. Si el submit llega <2s después, casi seguro es bot.
  const [honeypot, setHoneypot] = useState('');
  const [registerOpenedAt, setRegisterOpenedAt] = useState(null);
  // ── Cloudflare Turnstile — coverage extendido 2026-05-25 ──
  // Antes el widget se montaba SOLO en modo register; ahora se monta también
  // en modo login (con `action` distinto para que el backend pueda
  // diferenciar y rechazar reuso de un token de signup en un login).
  // La lógica de carga del script + render se movió a <TurnstileWidget />.
  // Si NEXT_PUBLIC_TURNSTILE_SITE_KEY no está, el widget no renderiza nada
  // y el token queda en '' (backend hace fail-open via el helper).
  const [turnstileToken, setTurnstileToken] = useState('');
  // BUG FIX (2026-05-27): los tokens de Turnstile son de un solo uso.
  // Si el form falla (credenciales incorrectas, rate limit, etc.), el token
  // ya fue consumido. En el reintento el mismo token llega al backend →
  // Cloudflare lo rechaza → "Validación de seguridad falló".
  // Solución: forzar remount del widget (key distinto) después de cualquier
  // error → token nuevo. El widget muestra "✓" del intento anterior aunque
  // ya no sirva — esto lo corrige.
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

  // Fuerza un token fresco: remonta el widget + limpia el token en state.
  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileResetKey(k => k + 1);
  };

  // Reset del token cuando el user togglea entre login y register — el token
  // emitido para una action no sirve para la otra (el backend valida action).
  useEffect(() => {
    setTurnstileToken('');
  }, [isRegister]);

  // Capturamos el momento en que el user activa el modo registro,
  // para medir el tiempo total que tardó en completar el form.
  useEffect(() => {
    if (isRegister && !registerOpenedAt) {
      setRegisterOpenedAt(Date.now());
      trackFbq('track', 'Lead', { content_name: 'register_form_opened' });
    } else if (!isRegister && registerOpenedAt) {
      setRegisterOpenedAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegister]);

  // 2FA flow: cuando el server responde requires_2fa: true tras
  // validar credenciales, pasamos a una vista para ingresar el código.
  const [pending2FA, setPending2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  // Passkey flow: si el user tiene >=1 passkey enrolada, el server
  // devuelve passkey_available=true. Mostramos botón para login con
  // llave de seguridad. Independiente del TOTP — pueden coexistir.
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  // 2026-05-25 fix: si el user solo tiene passkey (sin TOTP), el form
  // de código no aplica — lo ocultamos. Server señaliza via `requires_2fa`.
  const [hasTotp, setHasTotp] = useState(false);

  useEffect(() => {
    // Detectar si el browser soporta WebAuthn. Cargamos el helper de
    // @simplewebauthn/browser solo del lado cliente (es ESM puro).
    let mounted = true;
    (async () => {
      try {
        const mod = await import('@simplewebauthn/browser');
        if (!mounted) return;
        setPasskeySupported(!!mod.browserSupportsWebAuthn?.());
      } catch {
        if (mounted) setPasskeySupported(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // Al entrar al login, nos aseguramos de purgar cualquier acceso 2FA Master previo
    sessionStorage.removeItem('god_2fa_verified');
    
    if (searchParams.get('tab') === 'register') {
      setIsRegister(true);
    }
  }, [searchParams]);

  // Providers habilitados — requieren config en Supabase Dashboard
  // (Authentication → Providers → Google/Facebook → Enable + Client ID/Secret).
  const COMING_SOON_PROVIDERS = new Set([]);
  const SUPPORTED_OAUTH_PROVIDERS = new Set(['google', 'apple']);

  const handleSocialLogin = async (provider) => {
    if (COMING_SOON_PROVIDERS.has(provider)) {
      setComingSoonProvider(provider);
      return;
    }
    if (SUPPORTED_OAUTH_PROVIDERS.has(provider)) {
      // PENTEST FIX (Sprint 3 B / AUTH-04 hard-mode): ANTES de redirigir al
      // provider, llamar a /api/auth/oauth-start para que el server setee la
      // cookie HMAC `oauth_state` bound al UA. La cookie es one-time use y
      // sobrevive 5 min. Sin esto, un atacante con access_token leakado
      // (extensión, tab-napping, referer) puede replay-ear el login desde
      // su browser. Con la cookie en el browser legítimo, el callback
      // valida que el match es correcto y rechaza si no.
      //
      // Best-effort: si /oauth-start falla, seguimos al OAuth flow normal.
      // El callback en soft-mode (OAUTH_STATE_REQUIRED=false default) acepta
      // requests sin state cookie con audit log. Cuando el flag pase a true,
      // las requests sin cookie van a fallar — por eso es CRÍTICO que esta
      // llamada ocurra ANTES del redirect.
      try {
        await fetch('/api/auth/oauth-start', {
          method: 'POST',
          credentials: 'same-origin', // browser setea la cookie HttpOnly
        });
      } catch (e) {
        console.warn('[login] /oauth-start falló (degrade a soft-mode):', e?.message);
      }

      // CRÍTICO: redirectTo debe ser /auth/callback, NO /home.
      // Supabase pone los tokens en el HASH (#access_token=...) y nuestra
      // página /auth/callback los extrae, los manda a /api/auth/oauth-callback
      // para crear la sesión interna (cookie HMAC) y RECIÉN ahí redirige
      // al /home. Si pasamos /home directo, el hash llega pero nadie lo
      // procesa y el user queda sin sesión.
      supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
    } else {
      // Provider desconocido — fallback al modal de "próximamente"
      setComingSoonProvider(provider);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(totpCode.trim())) {
      setError('El código debe tener exactamente 6 dígitos.');
      return;
    }
    setIsLoading(true);
    try {
      const _did = getOrCreateDeviceId();
      const res = await fetch('/api/auth/2fa/login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: totpCode.trim(), deviceId: _did }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Device limit excedido tras 2FA OK — credenciales + TOTP válidos
        // pero la org ya llenó sus slots.
        if (data.code === 'DEVICE_LIMIT_REACHED') {
          setError(
            `${data.error || 'Límite de dispositivos alcanzado.'} ` +
            `Inicia sesión desde uno de los dispositivos que ya tienes activos y entra a Configuración → Dispositivos para revocar uno.`
          );
          setIsLoading(false);
          setPending2FA(false); // volver al form normal, no quedarse en 2FA loop
          return;
        }
        setError(data.error || 'Código inválido.');
        setIsLoading(false);
        return;
      }
      // Sesión establecida → ir al destino original (next) o al home.
      window.location.href = getSafeRedirectPath(searchParams);
    } catch (err) {
      setError('Error de conexión con el servidor.');
      setIsLoading(false);
    }
  };

  const handleCancel2FA = () => {
    // Vuelve a la vista de login. El cookie `dr_victor_2fa_pending` se
    // limpiará automáticamente por TTL (10 min) o cuando el usuario
    // intente loguearse de nuevo.
    setPending2FA(false);
    setTotpCode('');
    setPassword('');
    setError('');
    setPasskeyAvailable(false);
    setHasTotp(false);
  };

  // ── Passkey login handler ──
  // Pide auth-options al backend, llama navigator.credentials.get vía
  // @simplewebauthn/browser, manda el response a auth-verify. Si todo
  // OK, el backend setea cookie de sesión y redirigimos al destino.
  const handlePasskeyLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const optsRes = await fetch('/api/auth/webauthn/auth-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const opts = await optsRes.json();
      if (!optsRes.ok) {
        setError(opts.error || 'No se pudo iniciar el flujo de passkey.');
        setIsLoading(false);
        return;
      }
      let assertion;
      try {
        assertion = await startAuthentication({ optionsJSON: opts });
      } catch (e) {
        // Cancelación del prompt nativo o error del authenticator
        if (e?.name === 'NotAllowedError') {
          setError('Cancelaste la verificación con la llave.');
        } else {
          setError('No se pudo usar la llave de seguridad. Reintenta o usa otro método.');
        }
        setIsLoading(false);
        return;
      }
      const _did = getOrCreateDeviceId();
      const verifyRes = await fetch('/api/auth/webauthn/auth-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: assertion, deviceId: _did }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        if (verifyData.code === 'DEVICE_LIMIT_REACHED') {
          setError(
            `${verifyData.error || 'Límite de dispositivos alcanzado.'} ` +
            `Inicia sesión desde un dispositivo activo y entra a Configuración → Dispositivos.`
          );
          setPending2FA(false);
          setIsLoading(false);
          return;
        }
        setError(verifyData.error || 'No se pudo verificar la passkey.');
        setIsLoading(false);
        return;
      }
      window.location.href = getSafeRedirectPath(searchParams);
    } catch (err) {
      console.error('[passkey-login] error:', err);
      setError('Error de conexión con el servidor.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (isRegister && password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      setIsLoading(false);
      return;
    }

    if (isRegister && !acceptedTerms) {
      setError('Debes aceptar los Términos y Condiciones y la Política de Privacidad para crear la cuenta.');
      setIsLoading(false);
      return;
    }

    const token = searchParams.get('token');
    const plan = searchParams.get('plan') || 'basico';
    // FLOW PRUEBA GRATIS (2026-05-20, copy 3d 2026-06-17): el user puede llegar
    // a esta página por dos vías que significan "quiero probar gratis sin tarjeta":
    //   1. /planesbeta (o /planes?demo=true) → planes redirige con ?promo=demo1day
    //   2. CTA explícita "Comenzar Prueba de 3 Días" en landing → /planesbeta
    // ANTES solo se chequeaba `?demo=true` que NUNCA era seteado por las CTAs
    // públicas — disconnect que dejaba la prueba sin badge visible y forzaba
    // Stripe checkout inmediato contradiciendo el copy "sin tarjeta".
    // AHORA ambos params son honrados. El backend NO requiere flag isDemo: el
    // signup default crea membership pending_payment y, con TRIAL_3D_ENABLED on,
    // la sub pasa a trialing 3 días (5 si viene por referido). El flag local solo:
    //   (a) saltar Stripe checkout en el paso 3 (que sí pedía tarjeta),
    //   (b) mostrar badge UI "Probando 3 días gratis" (5 si ?ref=).
    const isDemoSignup = searchParams.get('demo') === 'true'
      || searchParams.get('promo') === 'demo1day';

    try {
      // 1. REGISTRO (Si aplica)
      if (isRegister) {
        const fillTimeMs = registerOpenedAt ? Date.now() - registerOpenedAt : 0;
        // WAR TEST 2026-05-21: device fingerprint para backend tracking de
        // multi-cuenta. Calculado solo cuando se registra (no en login normal).
        // Fingerprint es opaco (16 chars hex), no PII.
        const fingerprint = await getDeviceFingerprint().catch(() => null);
        const signupRes = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password, name, orgName, orgSize, jobTitle, country, phone, plan,
            // PR demographics-redesign (2026-05-22): campos opcionales.
            // El backend valida formato YYYY-MM-DD + edad ≥18 antes de
            // guardar. Si el user los dejó vacíos, van null y se ignoran.
            birth_date: birthDate || null,
            gender: gender || null,
            // P1 wave 2 (2026-05-24): onboarding personalization.
            // Backend valida contra enum permitido (familia/penal/laboral/etc).
            legal_specialty: legalSpecialty || null,
            primary_intent: primaryIntent || null,
            device_fingerprint: fingerprint,
            device_id: getOrCreateDeviceId(),
            // NOTA (2026-05-20): el flag `isDemo` se ELIMINÓ del API porque
            // producía un user roto (member_inactive_org, tokenAllowance=0).
            // El backend ahora siempre crea membership='pending_payment' y
            // classifyUser detecta <24h → demo_active automáticamente.
            // El flag local `isDemoSignup` se usa solo en esta página para
            // saltar Stripe checkout y mostrar el badge de prueba gratis.
            // El checkbox único de UI cubre lógicamente AMBAS aceptaciones
            // (el copy literal es "Términos y Condiciones y la Política de
            // Privacidad"). El backend exige los dos flags por separado, así
            // que los enviamos atados al mismo state `acceptedTerms`.
            acceptedTos: acceptedTerms,
            acceptedPrivacy: acceptedTerms,
            // Anti-bot in-house: honeypot debe estar vacío + el form debe
            // haber tardado al menos 2s en completarse.
            honeypot,
            fill_time_ms: fillTimeMs,
            // WAR TEST 2026-05-21: token de Cloudflare Turnstile si está activo.
            // Backend valida cuando TURNSTILE_SECRET_KEY env var está seteada.
            // Si no hay key, el token va vacío y el backend hace fail-open.
            turnstile_token: turnstileToken,
            // PR attribution-tracking-base (mig 182): mandamos UTMs + click IDs
            // capturados en el primer touch del usuario. Backend hace UPDATE
            // de profiles después del INSERT. Si localStorage está bloqueado
            // (modo privado estricto), getAttributionForSignup() devuelve null
            // y backend usa default 'direct'.
            attribution: getAttributionForSignup(),
          })
        });
        const signupData = await signupRes.json();
        if (!signupRes.ok) {
          const detail = signupData.details ? ` (${signupData.details})` : '';
          setError(`${signupData.error || 'Error en el registro.'}${detail}`);
          resetTurnstile();
          setIsLoading(false);
          return;
        }

        // REFACTOR 2026-05-26 (sec/signup-direct-session): el backend del signup
        // ahora setea la cookie de sesión directamente via finalizeUserSession
        // (mismo helper que usa /api/auth/login). Ya NO necesitamos el segundo
        // POST a /api/auth/login. Eso permitió endurecer login a Turnstile
        // fail-closed (sin softMissing).
        //
        // Casos:
        //   - signupData.auto_logged_in === true → estamos logueados, saltamos
        //     directo al step 3 (Stripe checkout o redirect final).
        //   - signupData.requires_login === true → el auto-login falló (típicamente
        //     por device limit o sessions concurrentes en la org del usuario).
        //     Mostramos modo login para que reintente con captcha fresco.
        if (signupData.requires_login) {
          setIsRegister(false);
          setError(signupData.message || 'Cuenta creada. Inicia sesión para continuar.');
          setIsLoading(false);
          trackFbq('track', 'CompleteRegistration', { status: 'requires_login' });
          return;
        }
        trackFbq('track', 'CompleteRegistration', { status: 'auto_logged_in' });
      } else {
        // 2. LOGIN (solo cuando el user vino directo, no después de signup).
        // Mandamos deviceId del browser para que el backend lo agregue al
        // array `trusted_devices` del profile. Y el turnstile_token del
        // widget montado en modo login (action='login').
        const deviceId = getOrCreateDeviceId();
        const loginRes = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            deviceId,
            turnstile_token: turnstileToken,
          })
        });
        const loginData = await loginRes.json();

        if (!loginRes.ok) {
          // Caso especial: device limit excedido. Las credenciales SON válidas
          // pero la org ya llenó sus slots de dispositivos. Mostramos info y un
          // mensaje claro para que el user vaya a otro device a revocar uno.
          if (loginData.code === 'DEVICE_LIMIT_REACHED') {
            setError(
              `${loginData.error || 'Límite de dispositivos alcanzado.'} ` +
              `Inicia sesión desde uno de los dispositivos que ya tienes activos y entra a Configuración → Dispositivos para revocar uno. ` +
              `Si eres owner de la organización, puedes revocar cualquier dispositivo de cualquier miembro desde ahí.`
            );
            resetTurnstile();
            setIsLoading(false);
            return;
          }
          setError(loginData.error || 'Credenciales inválidas.');
          resetTurnstile();
          setIsLoading(false);
          return;
        }

        // 2.5 — Si el usuario tiene 2do factor habilitado (TOTP y/o passkey),
        // el server NO setea la sesión todavía: dejó cookie `dr_victor_2fa_pending`
        // y nos pide completar 2do factor.
        if (loginData.requires_2fa || loginData.passkey_available) {
          setPending2FA(true);
          setPasskeyAvailable(!!loginData.passkey_available);
          setHasTotp(!!loginData.requires_2fa);
          setIsLoading(false);
          return;
        }

        // 2.6 — must_change_password legacy check (el backend actual no lo
        // devuelve pero dejamos el guard por defensa).
        if (loginData.requires_password_change) {
          window.location.href = loginData.redirect || '/cambiar-password';
          return;
        }
      }

      // 3. PASARELA DE PAGOS (Si es registro nuevo)
      if (isRegister) {
        const isBeta = searchParams.get('beta') === 'true';

        if (isBeta) {
          console.log("🌟 Acceso Beta detectado, saltando pasarela...");
        } else if (isDemoSignup) {
          // FLOW DEMO 1 DÍA (2026-05-20): el copy del landing prometía
          // "1 día sin tarjeta" pero el flujo redirigía a Stripe en este
          // punto — contradicción + bounce rate alto. Ahora, si el signup
          // vino del CTA "Demo 1 día", NO mostramos Stripe ahora: la
          // membership ya está pending_payment, classifyUser detecta <24h
          // → demo_active → user entra al dashboard como demo. Puede pagar
          // desde /suscripcion cuando se le acabe la demo o cuando decida.
          console.log("🎁 Demo 1 día detectada, saltando Stripe (el user paga después si quiere).");
        } else {
          console.log("💳 Redirigiendo a pasarela de pagos...");
          // 2026-06-07: si el user llegó con ?promo=X (deep link del banner
          // countdown LANZAMIENTO25 en /planesbeta), lo pasamos al checkout
          // para que Stripe lo aplique automáticamente. Si el código no existe
          // o expiró, el endpoint igual redirige a Stripe con `allow_promotion_codes`
          // y el user puede meterlo manualmente.
          const promoFromUrl = (searchParams.get('promo') || '').trim();
          const safePromo = promoFromUrl
            ? promoFromUrl.toUpperCase().replace(/[^A-Z0-9\-_]/g, '').slice(0, 30)
            : null;
          // Demo legacy NO va al checkout — el resto sí pasa el promo opcional.
          const checkoutBody = { planId: plan };
          if (safePromo && safePromo !== 'DEMO1DAY') {
            checkoutBody.promoCode = safePromo;
          }
          const payRes = await fetch('/api/billing/subscription-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(checkoutBody)
          });
          const payData = await payRes.json();
          if (payRes.ok && payData.url) {
            window.location.href = payData.url;
            return;
          } else {
            console.error("⚠️ Fallo al generar pasarela:", payData.error);
            setError(`Error en pagos: ${payData.error || 'No se pudo generar el enlace de Stripe.'}`);
            resetTurnstile();
            setIsLoading(false);
            return; // 🛑 BLOQUEO TOTAL. No avanza al /home
          }
        }
      }

      // 4. REDIRECCIÓN FINAL (login, beta, o demo signup)
      // Honrar ?next= si vino del dashboard (deep link tras sesión expirada)
      window.location.href = getSafeRedirectPath(searchParams);


    } catch (err) {
      setError('Error de conexión con el servidor.');
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.brandingSide}>
        <div className={styles.brandingContent}>
          <img src="/dvl-logo-transparent.svg" alt="Dr. Víctor Legal" width={180} height={180} className={styles.logoIcon} />
          <h1 className={styles.brandingTitle}>Dr. Víctor</h1>
          <p className={styles.brandingText}>
            El asistente legal definitivo. Diseñado con el cerebro del mejor abogado para ofrecerte inteligencia, seguridad y control absoluto sobre tu práctica jurídica.
          </p>
        </div>
        <div className={styles.overlay}></div>
      </div>

      <div className={styles.formSide}>
        <div className={styles.formWrapper}>
          {/* ── VISTA 2do factor — credenciales OK, falta TOTP o passkey ── */}
          {pending2FA ? (
            <>
              <div className={styles.formHeader}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: 'rgba(184, 150, 46, 0.1)',
                  border: '1px solid rgba(184, 150, 46, 0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1rem',
                }}>
                  <ShieldCheck size={28} weight="duotone" color="#d4af37" />
                </div>
                <h2>Verificación en 2 pasos</h2>
                <p>
                  {passkeyAvailable && passkeySupported && hasTotp
                    ? 'Confirma con tu llave de seguridad o usa el código del autenticador.'
                    : passkeyAvailable && passkeySupported
                      ? 'Confirma con tu llave de seguridad (Touch ID, Face ID, Windows Hello o llave física).'
                      : 'Ingresa el código de 6 dígitos de tu app autenticadora (Google Authenticator, Authy, 1Password).'}
                </p>
              </div>

              {/* PASSKEY: botón visible cuando el server dijo que hay
                  passkeys enroladas Y el browser soporta WebAuthn. */}
              {passkeyAvailable && passkeySupported && (
                <div style={{ marginBottom: '1rem' }}>
                  {error && <div className={styles.errorBanner}>{error}</div>}
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}
                    className={styles.submitBtn}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <ShieldCheck size={20} weight="bold" />
                    {isLoading ? 'Verificando llave…' : 'Iniciar sesión con llave'}
                  </button>
                  <p style={{
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.5)',
                    textAlign: 'center',
                    margin: '0.75rem 0 0',
                    lineHeight: 1.5,
                  }}>
                    Tu llave (Touch ID, Face ID, Windows Hello, YubiKey)
                    confirmará tu identidad sin necesidad de código.
                  </p>
                </div>
              )}

              {/* Form TOTP — solo visible si el user enroló TOTP. Si tiene
                  solo passkey, este bloque se oculta completamente (fix
                  2026-05-25: antes el form se mostraba siempre, generando
                  inputs vacíos que confundían a users con solo passkey). */}
              {hasTotp && (
                <form className={styles.loginForm} onSubmit={handleVerify2FA}>
                  {passkeyAvailable && passkeySupported && (
                    <div className={styles.divider}>
                      <span>o con código</span>
                    </div>
                  )}
                  {error && !(passkeyAvailable && passkeySupported) && <div className={styles.errorBanner}>{error}</div>}

                  <div className={styles.inputGroup}>
                    <label htmlFor="totp-code">Código de 6 dígitos</label>
                    <input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="••••••"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/[^\d]/g, ''))}
                      className={styles.inputField}
                      style={{
                        letterSpacing: '0.5em',
                        fontSize: '1.4rem',
                        textAlign: 'center',
                        fontFamily: 'monospace',
                      }}
                      autoFocus={!passkeyAvailable}
                      autoComplete="one-time-code"
                    />
                  </div>

                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={isLoading || totpCode.length !== 6}
                  >
                    {isLoading ? 'Verificando…' : 'Validar e ingresar'}
                  </button>

                  <button
                    type="button"
                    onClick={handleCancel2FA}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(184, 150, 46, 0.95)',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      marginTop: '0.5rem',
                      textDecoration: 'underline',
                    }}
                  >
                    Cancelar y volver al inicio de sesión
                  </button>

                  <div style={{
                    marginTop: '1.5rem',
                    padding: '0.75rem 1rem',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 10,
                    fontSize: '0.75rem',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: 1.5,
                  }}>
                    💡 ¿Perdiste acceso a tu autenticador? Contacta a tu administrador para que te
                    ayude a recuperar el acceso a la cuenta.
                  </div>
                </form>
              )}

              {/* Si user tiene solo passkey, mostramos el "Cancelar" igual
                  para volver al login. Sin el form arriba, el botón quedaría
                  huérfano si no lo proveemos acá. */}
              {!hasTotp && passkeyAvailable && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleCancel2FA}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'rgba(184, 150, 46, 0.95)',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Cancelar y volver al inicio de sesión
                  </button>
                </div>
              )}

              {/* Recovery: "¿Perdiste tu llave?" — visible cuando hay passkey.
                  Lleva al flow de forgot-password, que también limpia todas
                  las passkeys del user después del reset (audit hardening
                  2026-05-25). Único path realista para users con solo
                  passkey que perdieron el device. */}
              {passkeyAvailable && (
                <div style={{
                  marginTop: '1.25rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(184, 150, 46, 0.06)',
                  border: '1px solid rgba(184, 150, 46, 0.18)',
                  borderRadius: 10,
                  fontSize: '0.78rem',
                  color: 'rgba(255,255,255,0.75)',
                  lineHeight: 1.55,
                }}>
                  <strong style={{ color: '#d4af37' }}>¿Perdiste tu llave de seguridad?</strong>{' '}
                  Puedes{' '}
                  <a
                    href="/forgot-password"
                    style={{ color: '#d4af37', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    resetear tu contraseña por email
                  </a>
                  . Al hacerlo se revocan todas tus llaves para que puedas entrar y configurar una nueva.
                </div>
              )}
            </>
          ) : (
            <>
          <div className={styles.formHeader}>
            {/* FLOW DEMO 1 DÍA (2026-05-20): badge visible cuando el user
                vino del CTA "Demo 1 día" del landing/planesbeta. Reemplaza
                al "Plan Seleccionado" badge porque la demo NO compromete a
                un plan específico todavía — es prueba universal de 24h. */}
            {(searchParams.get('promo') === 'demo1day' || searchParams.get('demo') === 'true') ? (
              <div
                className={styles.planBadge}
                style={{
                  background: 'rgba(34, 197, 94, 0.12)',
                  borderColor: 'rgba(34, 197, 94, 0.35)',
                  color: '#22c55e',
                }}
              >
                🎁 Probando {searchParams.get('ref') ? '5' : '3'} días gratis · sin tarjeta
              </div>
            ) : searchParams.get('plan') && (
              <div className={styles.planBadge}>
                Plan Seleccionado: {searchParams.get('plan').toUpperCase()}
              </div>
            )}
            <h2>{isRegister ? 'Crear Cuenta' : 'Iniciar Sesión'}</h2>
            <p>{isRegister
              ? ((searchParams.get('promo') === 'demo1day' || searchParams.get('demo') === 'true')
                  ? `Activa tu acceso de ${searchParams.get('ref') ? '5' : '3'} días al plan Profesional. Vas a entrar directo sin pasarela de pagos.`
                  : 'Únete a la nueva era del derecho en RD.')
              : 'Accede de forma segura al panel de tu despacho.'}</p>
          </div>

          <form className={styles.loginForm} onSubmit={handleSubmit}>
            {error && <div className={styles.errorBanner}>{error}</div>}
            
            {isRegister && (
              <>
                <div className={styles.inputGroup}>
                  <label htmlFor="name">Nombre Completo</label>
                  <input 
                    type="text" 
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej. Juan Pérez" 
                    className={styles.inputField} 
                    required={isRegister}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="orgName">Firma o Despacho Legal</label>
                  <input 
                    type="text" 
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Ej. Pérez & Asociados" 
                    className={styles.inputField} 
                    required={isRegister}
                  />
                </div>

                <div className={styles.inputGrid}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="orgSize">Tamaño</label>
                    <select id="orgSize" value={orgSize} onChange={(e) => setOrgSize(e.target.value)} className={styles.inputField} required={isRegister}>
                      <option value="" disabled hidden>...</option>
                      <option value="1-5">1-5</option>
                      <option value="6-20">6-20</option>
                      <option value="50+">50+</option>
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="jobTitle">Cargo</label>
                    <input type="text" id="jobTitle" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Socio" className={styles.inputField} required={isRegister} />
                  </div>
                </div>

                {/* PR demographics-redesign (2026-05-22): captura demográfica
                    opcional al signup. Sin "required" — el user puede dejar
                    vacío. Trigger SQL deriva age_range desde birth_date. */}
                <div className={styles.inputGrid}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="birthDate">
                      Fecha de nacimiento{' '}
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>
                        opcional
                      </span>
                    </label>
                    <input
                      type="date"
                      id="birthDate"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className={styles.inputField}
                      max={new Date(Date.now() - 18 * 365.25 * 86400_000).toISOString().slice(0, 10)}
                      min="1900-01-01"
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="gender">
                      Género{' '}
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>
                        opcional
                      </span>
                    </label>
                    <select
                      id="gender"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className={styles.inputField}
                    >
                      <option value="">— seleccionar —</option>
                      <option value="F">Femenino</option>
                      <option value="M">Masculino</option>
                      <option value="NB">No binario</option>
                      <option value="NS">Prefiero no decir</option>
                    </select>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: '#94a3b8',
                    margin: '-4px 0 12px 0',
                    lineHeight: 1.4,
                  }}
                >
                  🔒 Usado solo para análisis agregados de uso y personalización de
                  contenido. Nunca mostramos tu edad o género individualmente.
                </div>

                {/* P1 wave 2 (2026-05-24): onboarding personalization fields.
                    Habilita recovery emails personalizados y dashboard sort
                    por relevancia. Opcionales — el user puede skipear. */}
                <div className={styles.inputGrid}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="legalSpecialty">
                      Especialidad legal{' '}
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>
                        opcional
                      </span>
                    </label>
                    <select
                      id="legalSpecialty"
                      value={legalSpecialty}
                      onChange={(e) => setLegalSpecialty(e.target.value)}
                      className={styles.inputField}
                    >
                      <option value="">— seleccionar —</option>
                      <option value="familia">Derecho de familia</option>
                      <option value="penal">Penal</option>
                      <option value="laboral">Laboral</option>
                      <option value="mercantil">Mercantil / Comercial</option>
                      <option value="civil">Civil</option>
                      <option value="administrativo">Administrativo</option>
                      <option value="tributario">Tributario</option>
                      <option value="constitucional">Constitucional</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="primaryIntent">
                      ¿Qué quieres hacer primero?{' '}
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>
                        opcional
                      </span>
                    </label>
                    <select
                      id="primaryIntent"
                      value={primaryIntent}
                      onChange={(e) => setPrimaryIntent(e.target.value)}
                      className={styles.inputField}
                    >
                      <option value="">— seleccionar —</option>
                      <option value="redactar_demanda">Redactar demandas / escritos</option>
                      <option value="consultar_jurisprudencia">Consultar jurisprudencia</option>
                      <option value="generar_contrato">Generar contratos</option>
                      <option value="organizar_expedientes">Organizar expedientes</option>
                      <option value="calcular_plazos">Calcular plazos procesales</option>
                      <option value="explorar">Solo explorar</option>
                      <option value="otro">Otra cosa</option>
                    </select>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: '#94a3b8',
                    margin: '-4px 0 12px 0',
                    lineHeight: 1.4,
                  }}
                >
                  ✨ Usamos esto para mostrarte las plantillas y casos más
                  relevantes desde el día 1. Cambiable después en tu perfil.
                </div>
              </>
            )}

            <div className={styles.inputGroup}>
              <label htmlFor="email">Correo Electrónico</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@drvictor.com"
                className={styles.inputField}
                required
                /* BUG FIX (2026-05) mobile: iOS no autocompleta del Keychain
                   sin autoComplete. autoCapitalize="off" previene mayúscula
                   automática en email. spellCheck=false evita "typo" rojo. */
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
              />
            </div>

            <div className={styles.inputGrid}>
              <div className={styles.inputGroup}>
                <label htmlFor="password">Contraseña</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={styles.inputField}
                    required
                    /* current-password para login | new-password para signup
                       (cambia abajo según isRegister, pero el password
                       manager tiene heuristics que detectan ambos) */
                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    aria-pressed={showPassword}
                    style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0.5rem' }}
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>

                </div>
              </div>

              {isRegister && (
                <div className={styles.inputGroup}>
                  <label htmlFor="confirmPassword">Confirmar</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className={styles.inputField}
                    required={isRegister}
                    autoComplete="new-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
              )}
            </div>

            {/* Link "Olvidé mi contraseña" — solo visible en login (no en registro) */}
            {!isRegister && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-0.5rem' }}>
                <a
                  href="/forgot-password"
                  style={{
                    fontSize: '0.8rem',
                    color: 'rgba(184, 150, 46, 0.85)',
                    textDecoration: 'none',
                    fontWeight: 600,
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#d4af37'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(184, 150, 46, 0.85)'}
                >
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
            )}

            {/* Checkbox aceptación T&C — solo en modo registro */}
            {isRegister && (
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
                fontSize: '0.8rem',
                color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.5,
                cursor: 'pointer',
                margin: '0.25rem 0',
              }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                  style={{
                    marginTop: 3,
                    width: 16,
                    height: 16,
                    accentColor: '#D4AF37',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
                <span>
                  Acepto los{' '}
                  <a
                    href="/legal/terminos"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#D4AF37', textDecoration: 'underline' }}
                  >
                    Términos y Condiciones
                  </a>
                  {' '}y la{' '}
                  <a
                    href="/legal/privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#D4AF37', textDecoration: 'underline' }}
                  >
                    Política de Privacidad
                  </a>
                  .
                </span>
              </label>
            )}

            {/* Honeypot — invisible para humanos. Los bots completan TODOS
                los inputs incluyendo este. tabIndex=-1 + autocomplete=off
                + aria-hidden ayudan a que screen readers también lo salten. */}
            {isRegister && (
              <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                <label>
                  No completes este campo
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>
            )}

            {/* Cloudflare Turnstile (coverage extendido 2026-05-25).
                Se monta en AMBOS modos (login y register) con `action`
                distinto para que el backend pueda rechazar reuso de token
                entre flows. Se desmonta al togglear isRegister (key=…) para
                regenerar token con la action correcta. */}
            {TURNSTILE_SITE_KEY && (
              <div style={{ margin: '0.5rem 0', display: 'flex', justifyContent: 'center' }}>
                <TurnstileWidget
                  key={`${isRegister ? 'signup' : 'login'}-${turnstileResetKey}`}
                  action={isRegister ? 'signup' : 'login'}
                  onToken={setTurnstileToken}
                  theme="dark"
                />
              </div>
            )}

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={
                isLoading
                || (isRegister && !acceptedTerms)
                // REFACTOR 2026-05-26: exigimos token Turnstile en AMBOS modos
                // (login y register). El backend del login es ahora fail-closed
                // (sin softMissing) gracias a que el signup setea sesión
                // directamente — ya no hay flow signup→login back-to-back que
                // necesitara softMissing. Sin token → captcha no cargó todavía
                // → no dejamos al user clickear (evita el agujero anterior).
                || (!!TURNSTILE_SITE_KEY && !turnstileToken)
              }
            >
              {isLoading
                ? 'Procesando...'
                : (!!TURNSTILE_SITE_KEY && !turnstileToken
                    ? 'Validando seguridad...'
                    : (isRegister ? 'Registrarse e Iniciar' : 'Entrar de forma segura'))}
            </button>

            
            <p className={styles.switchMode} onClick={() => setIsRegister(!isRegister)}>
              {isRegister ? '¿Ya tienes cuenta? Inicia sesión' : '¿Nuevo aquí? Crea tu cuenta élite'}
            </p>
          </form>

          <div className={styles.divider}>
            <span>o continuar con</span>
          </div>

          <div className={styles.socialGrid}>
            <button className={styles.socialBtn} title="Google" onClick={() => handleSocialLogin('google')} type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Google</span>
            </button>
            <button className={styles.socialBtn} title="Apple" onClick={() => handleSocialLogin('apple')} type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor"/>
              </svg>
              <span>Apple</span>
            </button>
          </div>

          <p className={styles.auditNotice}>
            <ShieldCheck size={20} weight="light" />
            Todas tus acciones quedarán registradas en la bitácora de auditoría forense.
          </p>
            </>
          )}
        </div>
      </div>

      {/* MODAL "Próximamente" para providers todavía no configurados */}
      {comingSoonProvider && (
        <ComingSoonModal
          provider={comingSoonProvider}
          onClose={() => setComingSoonProvider(null)}
        />
      )}
    </div>
  );
}

// ── Modal "Próximamente" ──
// Para providers cuya config en su dashboard externo (Meta, Apple, etc.)
// todavía no está completa. Mensaje suave + sugiere usar email/Google.
function ComingSoonModal({ provider, onClose }) {
  const labels = {
    facebook: { name: 'Facebook', color: '#1877F2', emoji: '📘' },
    apple: { name: 'Apple', color: '#000', emoji: '🍎' },
    twitter: { name: 'X / Twitter', color: '#000', emoji: '𝕏' },
    linkedin: { name: 'LinkedIn', color: '#0a66c2', emoji: '💼' },
  };
  const info = labels[provider] || { name: provider, color: '#71717a', emoji: '🔌' };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0f0f10', border: '1px solid #27272a',
          borderRadius: 18, padding: '2rem', maxWidth: 420, width: '100%',
          textAlign: 'center', boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
          animation: 'comingSoonIn 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{info.emoji}</div>
        <h3 style={{
          margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 800,
          marginBottom: '0.5rem',
        }}>
          {info.name} próximamente
        </h3>
        <p style={{
          color: '#a1a1aa', fontSize: '0.9rem', lineHeight: 1.5,
          margin: '0 0 1.5rem',
        }}>
          Estamos terminando la integración con {info.name}. Mientras tanto,
          puedes ingresar con <strong style={{ color: '#fff' }}>tu correo</strong> o
          con <strong style={{ color: '#fff' }}>Google</strong>.
        </p>
        <button
          onClick={onClose}
          style={{
            background: '#B8860B', border: 'none', color: '#fff',
            padding: '0.75rem 2rem', borderRadius: 10, fontWeight: 700,
            cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
            width: '100%',
          }}
        >
          Entendido
        </button>
        <style jsx>{`
          @keyframes comingSoonIn {
            from { transform: scale(0.92) translateY(8px); opacity: 0; }
            to { transform: scale(1) translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

export default function Login() {
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', backgroundColor: '#000c1d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Cargando portal...</div>}>
      <LoginContent />
    </Suspense>
  );
}
